import { InstancedBufferAttribute, InstancedBufferGeometry, Mesh, DynamicDrawUsage } from "three";
const DEBUG_LOG_ADDING_AND_REMOVING = false;
export const INSTANCE_INCREASE_FACTOR = 1.5;
const InverseColor = 1 / 255;
export class InstanceBatch {
    constructor(rootNode, geometry, material, attributes, renderOrder, instanceType, numInstancesInitial = 64, usage = DynamicDrawUsage, castShadow, receiveShadow, customDepthMaterial) {
        this.rootNode = rootNode;
        this.geometry = geometry;
        this.material = material;
        this.attributes = attributes;
        this.instanceType = instanceType;
        this.numInstancesInitial = numInstancesInitial;
        this.usage = usage;
        this.castShadow = castShadow;
        this.receiveShadow = receiveShadow;
        this.instancedGeometry = new InstancedBufferGeometry();
        this.numMinInstanced = 0;
        this.numMaxInstanced = 0;
        this.numInstanced = 0;
        this.numVisible = 0;
        this.attributeBuffer = [];
        this.instances = [];
        // clone attributes from geometry
        if (this.geometry.index != undefined) {
            this.instancedGeometry.index = this.geometry.index.clone();
        }
        for (const name in this.geometry.attributes) {
            const attribute = this.geometry.attributes[name];
            this.instancedGeometry.setAttribute(name, attribute.clone());
        }
        this.instancedGeometry.instanceCount = 0;
        this.instancedMesh = new Mesh(this.instancedGeometry, this.material);
        this.instancedMesh.frustumCulled = false;
        this.instancedMesh.matrixAutoUpdate = false;
        this.instancedMesh.matrixWorldNeedsUpdate = true;
        this.instancedMesh.renderOrder = renderOrder;
        if (customDepthMaterial) {
            ;
            this.instancedMesh.customDepthMaterial = customDepthMaterial;
        }
        this.instancedMesh.castShadow = castShadow || false;
        this.instancedMesh.receiveShadow = receiveShadow || false;
        this.rootNode.add(this.instancedMesh);
        this.instancedMesh.updateMatrixWorld(true);
        this.numMinInstanced = numInstancesInitial;
        this.build(numInstancesInitial);
    }
    isReady() {
        return this.numMaxInstanced > 0;
    }
    onNumVisibleChanged() {
        this.instancedGeometry.instanceCount = this.numVisible;
    }
    setMaterial(material, disableShadow) {
        this.material = material;
        if (this.instancedMesh) {
            this.instancedMesh.material = this.material;
        }
        if (disableShadow) {
            this.instancedMesh.castShadow = false;
            this.instancedMesh.receiveShadow = false;
        }
        else {
            this.instancedMesh.castShadow = !!this.castShadow;
            this.instancedMesh.receiveShadow = !!this.receiveShadow;
        }
    }
    getMesh() {
        return this.instancedMesh;
    }
    getMaterial() {
        return this.material;
    }
    setVisible(visible) {
        if (this.instancedMesh) {
            this.instancedMesh.visible = visible;
        }
    }
    getVisible() {
        return this.instancedMesh.visible;
    }
    getNumInstances() {
        return this.numInstanced;
    }
    createInstance(visible = true) {
        const instance = new this.instanceType(this, this.numInstanced);
        if (this.numInstanced >= this.numMaxInstanced - 1) {
            const targetSize = Math.ceil(this.numMaxInstanced * INSTANCE_INCREASE_FACTOR);
            this.rebuild(targetSize);
        }
        this.instances.push(instance);
        this.numInstanced++;
        if (visible) {
            if (this.numVisible < instance.instanceIndex) {
                this.swapEntity(this.numVisible, instance.instanceIndex);
            }
            this.numVisible++;
            this.onNumVisibleChanged();
        }
        DEBUG_LOG_ADDING_AND_REMOVING && console.log(`Instance added. Batch now has ${this.numInstanced}/${this.numMaxInstanced} instances.`);
        return instance;
    }
    deleteInstance(instance) {
        if (instance.instanceIndex < 0)
            return;
        // make invisible if not already => swapped to [numVisible]
        this.setVisibleAt(instance.instanceIndex, false);
        this.numInstanced--;
        this.swapEntity(instance.instanceIndex, this.numInstanced);
        this.instances.pop();
        instance.instanceIndex = -1;
        DEBUG_LOG_ADDING_AND_REMOVING && console.log(`Instance Batch removed and now has ${this.numInstanced}/${this.numMaxInstanced} instances`);
        if (this.numInstanced && this.numInstanced < this.numMaxInstanced / 2 && this.numInstanced > this.numMinInstanced) {
            this.rebuild(this.numInstanced);
        }
    }
    setVisibleAt(index, visible) {
        if (index < 0 || index >= this.numInstanced)
            return;
        if (visible) {
            if (this.numVisible < this.numInstanced && index >= this.numVisible) {
                if (index > this.numVisible) {
                    this.swapEntity(index, this.numVisible);
                }
                else {
                    // The instance is already at index this.numVisible, so we don't have to
                    // swap anything, but its data may not yet have been uploaded to the GPU,
                    // therfore we have to make sure all buffers are opdated correctly.
                    for (const data of this.attributeBuffer) {
                        data.dirtyIndices.add(index);
                    }
                }
                ++this.numVisible;
                this.onNumVisibleChanged();
            }
        }
        else if (this.numVisible > 0 && index < this.numVisible) {
            --this.numVisible;
            this.onNumVisibleChanged();
            this.swapEntity(index, this.numVisible);
        }
    }
    isVisibleAt(index) {
        return index < this.numVisible;
    }
    setXOfAttributeAt(attribute, index, x) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setX(index, x);
        data.dirtyIndices.add(index);
    }
    setYOfAttributeAt(attribute, index, y) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setY(index, y);
        data.dirtyIndices.add(index);
    }
    setZOfAttributeAt(attribute, index, z) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setZ(index, z);
        data.dirtyIndices.add(index);
    }
    setWOfAttributeAt(attribute, index, w) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setW(index, w);
        data.dirtyIndices.add(index);
    }
    setAttributeAt(attribute, index, v) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        switch (v.length) {
            case 4:
                data.buffer.setXYZW(index, v[0], v[1], v[2], v[3]);
                break;
            case 3:
                data.buffer.setXYZ(index, v[0], v[1], v[2]);
                break;
            case 2:
                data.buffer.setXY(index, v[0], v[1]);
                break;
            case 1:
                data.buffer.setX(index, v[0]);
                break;
        }
        data.dirtyIndices.add(index);
    }
    setColorAttributeAt(attribute, index, c) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setXYZ(index, Math.floor(c.r * 255), Math.floor(c.g * 255), Math.floor(c.b * 255));
        data.dirtyIndices.add(index);
    }
    setVector3AttributeAt(attribute, index, p) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setXYZ(index, p.x, p.y, p.z);
        data.dirtyIndices.add(index);
    }
    setQuaternionAttributeAt(attribute, index, q) {
        const data = this.getAttributeBuffer(attribute);
        if (!data)
            return;
        data.buffer.setXYZW(index, q.x, q.y, q.z, q.w);
        data.dirtyIndices.add(index);
    }
    getXOfAttributeAt(attribute, index) {
        const data = this.getAttributeBuffer(attribute);
        return data ? data.buffer.getX(index) : 0.0;
    }
    getYOfAttributeAt(attribute, index) {
        const data = this.getAttributeBuffer(attribute);
        return data ? data.buffer.getY(index) : 0.0;
    }
    getZOfAttributeAt(attribute, index) {
        const data = this.getAttributeBuffer(attribute);
        return data ? data.buffer.getZ(index) : 0.0;
    }
    getWOfAttributeAt(attribute, index) {
        const data = this.getAttributeBuffer(attribute);
        return data ? data.buffer.getW(index) : 0.0;
    }
    getColorAttributeAt(attribute, index, c) {
        const data = this.getAttributeBuffer(attribute);
        data && c.setRGB(data.buffer.getX(index) * InverseColor, data.buffer.getY(index) * InverseColor, data.buffer.getZ(index) * InverseColor);
    }
    getVector3AttributeAt(attribute, index, p) {
        const data = this.getAttributeBuffer(attribute);
        data && p.set(data.buffer.getX(index), data.buffer.getY(index), data.buffer.getZ(index));
    }
    getQuaternionAttributeAt(attribute, index, q) {
        const data = this.getAttributeBuffer(attribute);
        data && q.set(data.buffer.getX(index), data.buffer.getY(index), data.buffer.getZ(index), data.buffer.getW(index));
    }
    getAttributeBuffer(attribute) {
        if (attribute >= 0 && attribute < this.attributeBuffer.length) {
            return this.attributeBuffer[attribute];
        }
        return undefined;
    }
    swapAttributes(from, to) {
        for (const it of this.attributeBuffer) {
            const buffer = it.buffer;
            const size = it.size;
            const array = buffer.array;
            const fromOffset = from * size;
            const toOffset = to * size;
            for (let x = 0; x < size; ++x) {
                const fromIndex = fromOffset + x;
                const toIndex = toOffset + x;
                const old = array[toIndex];
                array[toIndex] = array[fromIndex];
                array[fromIndex] = old;
            }
            it.dirtyIndices.add(from);
            it.dirtyIndices.add(to);
        }
    }
    createAttributeBuffers() {
        this.attributeBuffer = [];
        for (const it of this.attributes) {
            const buffer = new InstancedBufferAttribute(new it.type(this.numMaxInstanced * it.size), it.size);
            this.instancedGeometry.setAttribute(it.name, buffer);
            buffer.setUsage(this.usage);
            buffer.normalized = it.normalized;
            this.attributeBuffer.push({
                buffer,
                size: it.size,
                dirtyIndices: new Set()
            });
        }
        // Three.js calculates the _maxInstanceCount based on the size of the attribute buffers.
        // However the value for _maxInstanceCount is not updated when we change the attribute buffers.
        // Therfore we have to force three.js to recalculate _maxInstanceCount by setting _maxInstanceCount to undefined.
        if (this.instancedGeometry._maxInstanceCount != undefined) {
            ;
            this.instancedGeometry._maxInstanceCount = undefined;
        }
    }
    swapEntity(from, to) {
        if (from === to) {
            // in case of the last element removal this may happen and we have nothing to do here
            return;
        }
        const instance = this.instances[from];
        instance.instanceIndex = to;
        this.instances[to].instanceIndex = from;
        this.swapAttributes(to, from);
        this.instances[from] = this.instances[to];
        this.instances[to] = instance;
    }
    build(numMaxInstanced) {
        this.numMaxInstanced = numMaxInstanced;
        this.createAttributeBuffers();
    }
    rebuild(numMaxInstanced) {
        const references = [];
        for (const it of this.attributeBuffer) {
            references.push(it.buffer);
        }
        this.build(numMaxInstanced);
        const length = Math.min(this.attributeBuffer.length, references.length);
        for (let i = 0; i < length; ++i) {
            this.copyAttributeBuffer(references[i], this.attributeBuffer[i].buffer);
        }
    }
    // should be called before you destroy the Batch
    destroy() {
        this.clear();
        this.instancedMesh.geometry.dispose();
        this.rootNode.remove(this.instancedMesh);
    }
    copyAttributeBuffer(src, dest) {
        if (dest.array.length >= src.array.length) {
            ;
            dest.array.set(src.array);
        }
        else {
            ;
            dest.array.set(src.array.subarray(0, dest.array.length));
        }
    }
    clearAttributeBuffers() {
        this.attributeBuffer = [];
    }
    clear() {
        this.numMaxInstanced = this.numInstancesInitial;
        this.numInstanced = 0;
        this.numVisible = 0;
        this.onNumVisibleChanged();
        this.instances = [];
        this.clearAttributeBuffers();
    }
    /** Queues uploads for changed visible instances, merging adjacent indices into component ranges. */
    update() {
        for (const it of this.attributeBuffer) {
            if (it.dirtyIndices.size === 0)
                continue;
            const indices = Array.from(it.dirtyIndices)
                .filter(index => index < this.numVisible)
                .sort((a, b) => a - b);
            it.dirtyIndices.clear();
            if (indices.length === 0)
                continue;
            let start = indices[0];
            let end = start;
            for (let i = 1; i < indices.length; i++) {
                const index = indices[i];
                if (index === end + 1) {
                    end = index;
                }
                else {
                    it.buffer.addUpdateRange(start * it.size, (end - start + 1) * it.size);
                    start = end = index;
                }
            }
            it.buffer.addUpdateRange(start * it.size, (end - start + 1) * it.size);
            // Keep earlier ranges until Three.js consumes them, even if update() runs again before rendering.
            it.buffer.needsUpdate = true;
        }
    }
}
